import React, { useContext, useEffect, useRef, useState } from "react";

import {
  Button,
  Modal,
  Steps,
  Input,
  InputNumber,
  Form,
  Result,
  Space,
  Select,
  Table,
  Popconfirm,
} from "antd";
import { useStepsForm } from "sunflower-antd";
import type { SelectProps, InputRef, FormInstance } from "antd";
import { AuthContext } from "../../../context/AuthContext";
import { toast } from "../../../utils/toast";

import { getCompanyFuneralProductsApi } from "../../../services/Funeral/FuneralProductApis";
import { getCompanyFuneralCustomersApi } from "../../../services/Funeral/FuneralCustomerApis";
import { getGroupApi } from "../../../services/Group/GroupApis";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  showModal: Function;
  handleAdd: Function;
}
const type: SelectProps["options"] = [
  {
    name: "Type",
    label: "Invoice",
    value: "Invoice",
  },
  {
    name: "Type",
    label: "Quote",
    value: "Quote",
  },
];

let customers: SelectProps["options"] = [];

let products: SelectProps["options"] = [];

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface Item {
  key: string;
  ProductName: string;
  Quantity: string;
  Description: string;
  Price: string;
}

interface EditableRowProps {
  index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext)!;

  useEffect(() => {
    if (editing) {
      inputRef.current!.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();

      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable && dataIndex === "ProductName") {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24 }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  } else if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24 }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

type EditableTableProps = Parameters<typeof Table>[0];

interface DataType {
  key: React.Key;
  ProductName: string;
  Quantity: string;
  Description: string;
  Price: string;
}

type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;

const AddFuneralInvoiceModal: React.FC<IModalProps> = ({
  show,
  showModal,
  handleAdd,
}) => {
  const { currentGroup } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [quantity, setQuantity] = useState("");
  const [grossTotal, setGrossTotal] = useState(0);
  const [nettTotal, setNettTotal] = useState(0);
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatTotal, setVatTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchData = async () => {
    const productData = await getCompanyFuneralProductsApi(currentGroup);
    
    if (!productData.data || productData.data.length === 0) {
      toast.warning({
        title: 'No Funeral Products Configured',
        description: 'This group has no funeral products set up. Please configure funeral products before creating invoices.',
        duration: 8,
      });
      products = [];
    } else {
      const filteredProducts = productData.data.filter(
        (product: any) =>
          !selectedProducts.some(
            (selectedProduct) => selectedProduct.key === product.id
          )
      );

      products = filteredProducts.map((product: any) => {
        return {
          value: product.id,
          label: product.Name,
          description: product.Description, // Additional data
          price: product.Price, // Additional data
        };
      });
    }

    const customerData = await getCompanyFuneralCustomersApi(currentGroup);
    // const filteredCustomers = customerData.data.filter(
    //   (customer: any) => !customer.FuneralInvoice
    // );

    customers = customerData.data.map((customer: any) => {
      return {
        value: customer.id,
        label: `${customer.Name} ${customer.Surname}`,
        name: customer.Name,
        surname: customer.Surname,
        idNumber: customer.IdNumber,
        cellphone: customer.Cellphone,
        addressLine1: customer.AddressLine1,
        addressLine2: customer.AddressLine2,
        postalCode: customer.PostalCode,
      };
    });
  };

  const handleInvoiceQuoteModal = () => {
    showModal();
    gotoStep(0);
    setSelectedProducts([]);
    setDataSource([]);
    setGrossTotal(0);
    setVatTotal(0);
    setNettTotal(0);
    form.resetFields();
  };

  const handleDelete = (key: React.Key) => {
    const newData = dataSource.filter((item) => item.key !== key);
    setDataSource(newData);
    setSelectedProducts(newData);

    // Recalculate the totals
    let newGrossTotal = newData.reduce(
      (total, item: any) => total + item.Price * item.Quantity,
      0
    );
    let newVatTotal = vatEnabled
      ? Number((newGrossTotal * 0.15).toFixed(2))
      : 0;
    let newNettTotal = newGrossTotal + newVatTotal;

    setGrossTotal(newGrossTotal);
    setVatTotal(newVatTotal);
    setNettTotal(newNettTotal);
  };

  const defaultColumns: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
  })[] = [
    {
      title: "Product Name",
      dataIndex: "ProductName",
    },
    {
      title: "Qty",
      dataIndex: "Quantity",
      editable: true,
    },
    {
      title: "Description",
      dataIndex: "Description",
    },
    {
      title: "Item Price",
      dataIndex: "Price",
      render: (_, record: any) => String("R " + record.Price),
    },
    {
      title: "Total",
      dataIndex: "Price",
      render: (_, record: any) =>
        String("R " + Number(record.Quantity) * record.Price),
    },
    {
      dataIndex: "operation",
      render: (_, record: any) =>
        dataSource.length >= 1 ? (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => handleDelete(record.key)}
          >
            <a>Remove</a>
          </Popconfirm>
        ) : null,
    },
  ];

  const handleAddRow = () => {
    setModalVisible(true);
  };

  const handleProductModalOk = () => {
    // Here you can add the selected product and other info to the dataSource
    const newData: DataType = {
      key: selectedProduct?.value,
      ProductName: selectedProduct?.label,
      Quantity: quantity,
      Description: selectedProduct?.description,
      Price: selectedProduct?.price,
    };

    // Calculate the total before updating dataSource
    let grosstotal = 0;

    dataSource.forEach((item: any) => {
      grosstotal += item.Price * item.Quantity;
    });

    setDataSource([...dataSource, newData]);

    setSelectedProducts([
      ...selectedProducts,
      {
        key: selectedProduct.value,
        ProductName: selectedProduct.label,
        Quantity: quantity,
        Description: selectedProduct.description,
        Price: selectedProduct.price,
      },
    ]);

    // Update grossTotal
    setGrossTotal(
      grosstotal + (selectedProduct?.price || 0) * Number(quantity)
    );

    setSelectedProduct(null);
    setSelectedCustomer(null);
    setQuantity("");

    setModalVisible(false);
  };

  const handleProductModalCancel = () => {
    setSelectedProduct(null);
    setSelectedCustomer(null);
    setQuantity("");
    setModalVisible(false);
  };

  const handleSave = (row: DataType) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    async submit(values: any) {
      setLoading(true);

      const response = await handleAdd({
        ...values,
        GrossTotal: grossTotal,
        OutstandingBalance: nettTotal,
        NettTotal: nettTotal,
        VATTotal: vatTotal,
        Products: dataSource,
      });

      if (response.success) {
        setLoading(false);
        return true;
      }
      setErrorMsg(response.message);
      setLoading(false);
      return false;
    },
    total: 5,
  });

  useEffect(() => {
    fetchData();
  }, [handleAddRow]);

  useEffect(() => {
    getGroupApi(currentGroup)
      .then((res) => {
        setVatEnabled(res.data.VATEnabled);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // Step Styling
  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 17 },
    labelAlign: "right" as const,
  };

  const tailLayout = {
    labelCol: { span: 4 },
    wrapperCol: { offset: 4, span: 19 },
  };

  const formList = [
    <>
      <Form.Item
        name="Type"
        label="Type"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          options={type}
          style={{ width: "100%" }}
          size="large"
          placeholder="Select Type"
        />
      </Form.Item>

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Space>
      </Form.Item>
    </>,

    <>
      <Form.Item
        name="Customer"
        label="Customer"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          options={customers}
          style={{ width: "100%" }}
          size="large"
          onChange={(value, option) => setSelectedCustomer(option)}
          placeholder="Select Customer"
        />
      </Form.Item>
      {/* {selectedCustomer && (
        <>
          <Form.Item
            name="Name"
            label="Name"
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={selectedCustomer.name}
          >
            <Input size="large" placeholder="Name" />
          </Form.Item>

          <Form.Item
            name="Surname"
            label="Surname"
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={selectedCustomer.surname}
          >
            <Input size="large" placeholder="Surname" />
          </Form.Item>

          <Form.Item
            name="IdNumber"
            label="ID Number"
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={selectedCustomer.idNumber}
          >
            <Input size="large" placeholder="ID Number" />
          </Form.Item>

          <Form.Item
            name="Cellphone"
            label="Cellphone"
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={selectedCustomer.cellphone}
          >
            <Input size="large" placeholder="Cellphone" />
          </Form.Item>

          <Form.Item
            name="AddressLine1"
            label="Address Line 1 "
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={selectedCustomer.addressLine1}
          >
            <Input size="large" placeholder="Address Line 1" />
          </Form.Item>
          <Form.Item
            name="AddressLine2"
            label="Address Line 2"
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={selectedCustomer.addressLine2}
          >
            <Input size="large" placeholder="Address Line 2" />
          </Form.Item>
          <Form.Item
            name="PostalCode"
            label="Postal Code"
            rules={[
              {
                required: true,
              },
            ]}
            initialValue={selectedCustomer.postalCode}
          >
            <Input size="large" placeholder="Postal Code" />
          </Form.Item>
        </>
      )} */}
      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Space>
      </Form.Item>
    </>,
    <>
      <Button
        onClick={handleAddRow}
        type="primary"
        style={{ marginBottom: 16 }}
      >
        Add a Product
      </Button>

      <Modal
        title="Add Product"
        open={modalVisible}
        onOk={handleProductModalOk}
        onCancel={handleProductModalCancel}
      >
        <Form layout="vertical">
          <Form.Item label="Product">
            <Select
              value={selectedProduct ? selectedProduct.value : undefined} // Ensures the Select value is reset
              options={products}
              onChange={(value, option) => setSelectedProduct(option)}
              style={{ width: "100%" }}
              placeholder="Select Product"
            />
          </Form.Item>

          <Form.Item label="Quantity">
            <InputNumber
              value={quantity} // Ensures the Input value is reset
              onChange={(e: any) => setQuantity(e)}
              style={{ width: "100%" }}
              placeholder="Select Quantity"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Table
        style={{ marginBottom: 16 }}
        components={components}
        rowClassName={() => "editable-row"}
        bordered
        dataSource={dataSource}
        columns={columns as ColumnTypes}
        pagination={false}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}>
                <strong>Gross Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>R {grossTotal}</Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
            </Table.Summary.Row>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}>
                <strong>VAT </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                R{" "}
                {vatEnabled !== false
                  ? Number(Number(grossTotal * 0.15).toFixed(2))
                  : 0}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
            </Table.Summary.Row>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={0}>
                <strong>Nett Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                R{" "}
                {vatEnabled !== false
                  ? grossTotal + Number(Number(grossTotal * 0.15).toFixed(2))
                  : grossTotal}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            style={{ marginBottom: 10 }}
            block
            size="large"
            loading={formLoading}
            onClick={() => {
              submit().then((result) => {
                if (result) {
                  gotoStep(current + 1);
                } else if (!result) {
                  gotoStep(current + 2);
                }
              });
            }}
          >
            Submit
          </Button>
        </Space>
      </Form.Item>
    </>,
  ];

  return (
    <Modal
      title="New Invoice/Quote"
      open={show}
      onOk={handleInvoiceQuoteModal}
      onCancel={handleInvoiceQuoteModal}
      width={900}
      footer={null}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 3 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Invoice/Quote" />
              <Step title="Customer Information" />
              <Step title="Product Information" />
            </Steps>
          </div>
        )}
        <div style={{ flex: 1, marginTop: 24 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 1100 }}>
            {formList[current]}
          </Form>

          {current === 3 && (
            <Result status="success" title="Successfully submitted">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  block
                  size="large"
                  onClick={handleInvoiceQuoteModal}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 4 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  block
                  size="large"
                  onClick={handleInvoiceQuoteModal}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddFuneralInvoiceModal;
